/**
 * Owner: Masa Bayu
 * File: create-qxread-design-system.ts
 * Date:Time: 2026-09-15 20:00
 * Description: QXRead marketplace design-system instance consuming @qx/ui foundation.
 */

import {
  createQXColorSwatch,
  createQXColorSystem,
  createQXDesignBorderSystem,
  createQXDesignBorderToken,
  createQXDesignSystem,
  createQXDesignSystemIdentity,
  createQXElevationSystem,
  createQXElevationToken,
  createQXFontFamily,
  createQXShapeSystem,
  createQXShapeToken,
  createQXSpacingSystem,
  createQXSpacingToken,
  createQXTheme,
  createQXThemeColorMapping,
  createQXThemeReference,
  createQXTypographyLetterSpacingScale,
  createQXTypographyLetterSpacingToken,
  createQXTypographyLineHeightScale,
  createQXTypographyLineHeightToken,
  createQXTypographyRoleStyle,
  createQXTypographySizeScale,
  createQXTypographySizeToken,
  createQXTypographySystem,
  validateQXDesignSystem,
  type QXDesignSystem,
} from '@qx/ui';

export const QXREAD_DESIGN_SYSTEM_ID = 'qxread-marketplace' as const;

/**
 * Builds the QXRead product skin on the shared @qx/ui foundation.
 * Semantic only — CSS presentation is applied by the app bridge.
 */
export function createQxreadDesignSystem(): QXDesignSystem {
  const system = createQXDesignSystem({
    identity: createQXDesignSystemIdentity({
      id: QXREAD_DESIGN_SYSTEM_ID,
      name: 'QXRead Marketplace',
      version: '0.1.0',
    }),
    color: createQXColorSystem({
      swatches: [
        createQXColorSwatch({ id: 'swatch-bg', name: 'Canvas' }),
        createQXColorSwatch({ id: 'swatch-surface', name: 'Elevated' }),
        createQXColorSwatch({ id: 'swatch-fg', name: 'Ink' }),
        createQXColorSwatch({ id: 'swatch-fg-muted', name: 'Ink Muted' }),
        createQXColorSwatch({ id: 'swatch-primary', name: 'Brand Teal' }),
        createQXColorSwatch({ id: 'swatch-secondary', name: 'Brand Secondary' }),
        createQXColorSwatch({ id: 'swatch-accent', name: 'Accent' }),
        createQXColorSwatch({ id: 'swatch-border', name: 'Border' }),
        createQXColorSwatch({ id: 'swatch-divider', name: 'Divider' }),
        createQXColorSwatch({ id: 'swatch-success', name: 'Success' }),
        createQXColorSwatch({ id: 'swatch-warning', name: 'Warning' }),
        createQXColorSwatch({ id: 'swatch-danger', name: 'Danger' }),
        createQXColorSwatch({ id: 'swatch-paper', name: 'Publication Paper' }),
      ],
      themes: [
        createQXTheme({
          id: 'qxread-light',
          name: 'QXRead Light',
          mappings: [
            createQXThemeColorMapping('background', 'swatch-bg'),
            createQXThemeColorMapping('surface', 'swatch-surface'),
            createQXThemeColorMapping('surface-elevated', 'swatch-surface'),
            createQXThemeColorMapping('foreground', 'swatch-fg'),
            createQXThemeColorMapping('foreground-muted', 'swatch-fg-muted'),
            createQXThemeColorMapping('primary', 'swatch-primary'),
            createQXThemeColorMapping('secondary', 'swatch-secondary'),
            createQXThemeColorMapping('accent', 'swatch-accent'),
            createQXThemeColorMapping('border', 'swatch-border'),
            createQXThemeColorMapping('divider', 'swatch-divider'),
            createQXThemeColorMapping('success', 'swatch-success'),
            createQXThemeColorMapping('warning', 'swatch-warning'),
            createQXThemeColorMapping('danger', 'swatch-danger'),
          ],
        }),
      ],
      defaultTheme: createQXThemeReference('qxread-light'),
    }),
    typography: createQXTypographySystem({
      fonts: [
        createQXFontFamily({ id: 'qxread-display', name: 'Syne', kind: 'sans' }),
        createQXFontFamily({ id: 'qxread-sans', name: 'Figtree', kind: 'sans' }),
      ],
      sizeScale: createQXTypographySizeScale([
        createQXTypographySizeToken('sm', 2),
        createQXTypographySizeToken('md', 3),
        createQXTypographySizeToken('lg', 4),
        createQXTypographySizeToken('xl', 6),
        createQXTypographySizeToken('4xl', 8),
      ]),
      lineHeightScale: createQXTypographyLineHeightScale([
        createQXTypographyLineHeightToken('tight', 3),
        createQXTypographyLineHeightToken('snug', 3),
        createQXTypographyLineHeightToken('normal', 4),
        createQXTypographyLineHeightToken('relaxed', 5),
      ]),
      letterSpacingScale: createQXTypographyLetterSpacingScale([
        createQXTypographyLetterSpacingToken('tight', -1),
        createQXTypographyLetterSpacingToken('normal', 0),
        createQXTypographyLetterSpacingToken('wide', 1),
      ]),
      roles: [
        createQXTypographyRoleStyle({
          role: 'display',
          fontFamilyId: 'qxread-display',
          sizeStep: '4xl',
          weight: 'bold',
          lineHeightStep: 'tight',
          letterSpacingStep: 'tight',
        }),
        createQXTypographyRoleStyle({
          role: 'title',
          fontFamilyId: 'qxread-display',
          sizeStep: 'xl',
          weight: 'semibold',
          lineHeightStep: 'tight',
          letterSpacingStep: 'tight',
        }),
        createQXTypographyRoleStyle({
          role: 'body',
          fontFamilyId: 'qxread-sans',
          sizeStep: 'md',
          weight: 'regular',
          lineHeightStep: 'normal',
          letterSpacingStep: 'normal',
        }),
        createQXTypographyRoleStyle({
          role: 'label',
          fontFamilyId: 'qxread-sans',
          sizeStep: 'sm',
          weight: 'medium',
          lineHeightStep: 'normal',
          letterSpacingStep: 'wide',
        }),
        createQXTypographyRoleStyle({
          role: 'caption',
          fontFamilyId: 'qxread-sans',
          sizeStep: 'sm',
          weight: 'regular',
          lineHeightStep: 'normal',
          letterSpacingStep: 'normal',
        }),
      ],
    }),
    spacing: createQXSpacingSystem({
      spacings: [
        createQXSpacingToken('none', 0),
        createQXSpacingToken('xs', 1),
        createQXSpacingToken('sm', 2),
        createQXSpacingToken('md', 3),
        createQXSpacingToken('lg', 4),
        createQXSpacingToken('xl', 5),
      ],
    }),
    shape: createQXShapeSystem({
      shapes: [
        createQXShapeToken('none', 0),
        createQXShapeToken('small', 1),
        createQXShapeToken('medium', 2),
        createQXShapeToken('large', 3),
      ],
    }),
    border: createQXDesignBorderSystem([
      createQXDesignBorderToken({
        role: 'none',
        widthStep: 0,
        style: 'none',
        colorRole: 'border',
      }),
      createQXDesignBorderToken({
        role: 'subtle',
        widthStep: 1,
        style: 'solid',
        colorRole: 'border',
      }),
      createQXDesignBorderToken({
        role: 'divider',
        widthStep: 1,
        style: 'solid',
        colorRole: 'divider',
      }),
      createQXDesignBorderToken({
        role: 'strong',
        widthStep: 2,
        style: 'solid',
        colorRole: 'border',
      }),
      createQXDesignBorderToken({
        role: 'focus',
        widthStep: 2,
        style: 'solid',
        colorRole: 'primary',
      }),
    ]),
    elevation: createQXElevationSystem({
      elevations: [
        createQXElevationToken('none', 0),
        createQXElevationToken('low', 1),
        createQXElevationToken('medium', 2),
        createQXElevationToken('high', 3),
      ],
    }),
  });
  validateQXDesignSystem(system);
  return system;
}
